import { createContext, ReactNode, useCallback, useMemo, useState } from 'react'

import {
  PublicSiteSettings,
  SanityCart,
  SanitySiteFragment,
} from '@data/sanity/queries/types/site'
import { SanityVideo } from '@data/sanity/queries/types/video'
import { isBrowser } from '@lib/helpers'

type MegaNavUpdateState = boolean | 'toggle'

interface SiteContextMegaNavigation {
  isOpen: boolean
  activeId?: string
}

interface SiteContextVideoModal {
  isOpen: boolean
  video?: SanityVideo
}

interface SiteContextMobileMenu {
  isOpen: boolean
}

interface SiteContextProps {
  isPageTransition: boolean
  megaNavigation: SiteContextMegaNavigation
  mobileMenu: SiteContextMobileMenu
  videoModal: SiteContextVideoModal
  settings: PublicSiteSettings | null
  cartSettings: SanityCart | null
  togglePageTransition: (newState: boolean) => void
  toggleMegaNavigation: (updateState: MegaNavUpdateState, id?: string) => void
  toggleMobileMenu: (isOpen: boolean) => void
  toggleVideoModal: (isOpen: boolean, video?: SanityVideo) => void
  getProductCount: (collectionSlug?: string) => number
}

const initialSiteContext: SiteContextProps = {
  isPageTransition: false,
  megaNavigation: {
    isOpen: false,
  },
  mobileMenu: {
    isOpen: false,
  },
  videoModal: {
    isOpen: false,
  },
  settings: null,
  cartSettings: null,
  togglePageTransition: () => null,
  toggleMegaNavigation: () => null,
  toggleMobileMenu: () => null,
  toggleVideoModal: () => null,
  getProductCount: () => 0,
}

interface SiteContextProviderProps {
  site: SanitySiteFragment
  children: ReactNode
}

export const SiteContext = createContext<SiteContextProps>(initialSiteContext)

export const SiteContextProvider = ({
  site,
  children,
}: SiteContextProviderProps) => {
  // State variables
  const [isPageTransition, setIsPageTransition] = useState<boolean>(
    initialSiteContext.isPageTransition,
  )
  const [mobileMenu, setMobileMenu] = useState<SiteContextMobileMenu>(
    initialSiteContext.mobileMenu,
  )
  const [megaNavigation, setMegaNavigation] =
    useState<SiteContextMegaNavigation>(initialSiteContext.megaNavigation)
  const [videoModal, setVideoModal] = useState<SiteContextVideoModal>(
    initialSiteContext.videoModal,
  )

  const productCounts = useMemo(() => {
    return site.productCounts
  }, [site])

  const settings = useMemo(() => {
    return site.settings
  }, [site])

  const cartSettings = useMemo(() => {
    return site.cart
  }, [site])

  // Update callbacks
  const togglePageTransition = useCallback((newState: boolean) => {
    setIsPageTransition(newState)
  }, [])

  const toggleMegaNavigation = useCallback(
    (updateState: MegaNavUpdateState, id?: string) => {
      const getActiveId = (newState: boolean, id?: string) => {
        if (newState) {
          return id
        }
      }

      const isOpen =
        updateState === 'toggle' ? !megaNavigation.isOpen : updateState
      const activeId = getActiveId(isOpen, id)

      if (
        megaNavigation.isOpen !== isOpen ||
        megaNavigation.activeId !== activeId
      ) {
        setMegaNavigation({
          isOpen,
          activeId,
        })
      }
    },
    [megaNavigation],
  )

  const toggleMobileMenu = useCallback(
    (isOpen: boolean) => {
      if (mobileMenu.isOpen !== isOpen) {
        setMobileMenu({
          isOpen,
        })
      }

      if (isBrowser) {
        document.body.classList.toggle('overflow-hidden', isOpen)
      }
    },
    [mobileMenu.isOpen],
  )

  const toggleVideoModal = useCallback(
    (isOpen: boolean, video?: SanityVideo) => {
      setVideoModal({
        isOpen,
        video,
      })
    },
    [],
  )

  const getProductCount = useCallback(
    (collectionSlug?: string) => {
      const productCount = productCounts?.find(
        (productCount) => productCount.slug === collectionSlug,
      )

      return productCount?.count ?? 0
    },
    [productCounts],
  )

  return (
    <SiteContext.Provider
      value={{
        isPageTransition,
        megaNavigation,
        mobileMenu,
        videoModal,
        settings,
        cartSettings,
        togglePageTransition,
        toggleMegaNavigation,
        toggleMobileMenu,
        toggleVideoModal,
        getProductCount,
      }}
    >
      {children}
    </SiteContext.Provider>
  )
}
